import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Select, Badge, Dropdown, Button, Drawer } from 'antd';
import { ReloadOutlined, BellOutlined, MenuOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { api } from '@/lib/api';
import { useBreakpoint } from '@/lib/breakpoints';
import { notReadyWeekSet } from '@/lib/weekData';
import { useAuthStore } from '@/stores/authStore';
import { useWeekStore } from '@/stores/weekStore';

const { Header, Sider, Content } = Layout;

// =========================================================
// 菜单数据（emoji + 路径 + 可选 badge）
// =========================================================
type NavGroup = {
  title: string;
  items: Array<{
    to: string;
    icon: string;
    label: string;
    badge?: ReactNode;
  }>;
};

const NAV_GROUPS: NavGroup[] = [
  {
    title: '工作台',
    items: [
      { to: '/overview', icon: '📊', label: '首页总览' },
      {
        to: '/agent',
        icon: '🤖',
        label: '高管周报 (Agent)',
        badge: (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-violet-100 text-violet-700 font-medium">
            AI
          </span>
        ),
      },
    ],
  },
  {
    title: '品牌洞察',
    items: [
      { to: '/brands', icon: '🏷️', label: '品牌综合口碑总表' },
      { to: '/brands/GAC-AION', icon: '📈', label: '品牌详情' },
    ],
  },
  {
    title: '车型口碑',
    items: [
      { to: '/models', icon: '🚗', label: '车型评分列表' },
      { to: '/models/M-AION-S', icon: '📝', label: '车型详情' },
    ],
  },
  {
    title: '投诉与召回',
    items: [
      { to: '/complaints', icon: '⚠️', label: '投诉问题矩阵' },
      { to: '/recalls', icon: '📢', label: '官方召回库' },
    ],
  },
  {
    title: '风险预警',
    items: [
      {
        to: '/alerts',
        icon: '🚨',
        label: '预警中心',
        badge: (
          <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-red-500 text-white font-bold min-w-[18px] text-center">
            5
          </span>
        ),
      },
    ],
  },
  {
    // PR3-C-2: 社交传播风险（authenticated 非 admin）——独立组，**不在**「配置中心」admin-only 组
    title: '社交信号',
    items: [{ to: '/social-risk', icon: '📡', label: '社交传播风险' }],
  },
  {
    title: '配置中心',
    items: [
      { to: '/data-sources', icon: '🌐', label: '数据源管理' },
      { to: '/tasks', icon: '⏱️', label: '抓取任务监控' },
      { to: '/catalog', icon: '📚', label: '车型字典' },
      { to: '/settings', icon: '⚙️', label: '系统设置' },
    ],
  },
];

// =========================================================
// 面包屑映射
// =========================================================
const PATH_LABEL: Record<string, { group: string; page: string }> = {
  '/overview': { group: '工作台', page: '首页总览' },
  '/agent': { group: '工作台', page: '高管周报 (Agent)' },
  '/brands': { group: '品牌洞察', page: '品牌综合口碑总表' },
  '/models': { group: '车型口碑', page: '车型评分列表' },
  '/complaints': { group: '投诉与召回', page: '投诉问题矩阵' },
  '/recalls': { group: '投诉与召回', page: '官方召回库' },
  '/alerts': { group: '风险预警', page: '预警中心' },
  '/data-sources': { group: '配置中心', page: '数据源管理' },
  '/tasks': { group: '配置中心', page: '抓取任务监控' },
  '/catalog': { group: '配置中心', page: '车型字典' },
  '/settings': { group: '配置中心', page: '系统设置' },
};

// =========================================================
// 一个 nav 菜单项
// =========================================================
function NavItem({
  icon,
  label,
  badge,
  active,
  onClick,
}: {
  icon: string;
  label: string;
  badge?: ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`
        flex items-center cursor-pointer rounded-lg transition-colors
        text-[13.5px]
        ${
          active
            ? 'text-white font-medium'
            : 'text-slate-300 hover:bg-white/[0.08] hover:text-white'
        }
      `}
      style={
        active
          ? {
              padding: '9px 14px 9px 11px',
              borderLeft: '3px solid #2E7CE4',
              background:
                'linear-gradient(90deg, rgba(46,124,228,0.25), rgba(46,124,228,0.05))',
            }
          : { padding: '9px 14px' }
      }
    >
      <span className="w-5 mr-2 flex-shrink-0">{icon}</span>
      <span className="flex-1">{label}</span>
      {badge && <span className="ml-2">{badge}</span>}
    </div>
  );
}

// =========================================================
// 导航内容（Sider 与 Drawer 共用,9.2f）
// onNavigate 后由调用方决定是否关闭 Drawer
// =========================================================
function SideNav({
  isActive,
  onNavigate,
  groups,
}: {
  isActive: (to: string) => boolean;
  onNavigate: (to: string) => void;
  groups: NavGroup[];
}) {
  return (
    <>
      <nav className="py-3 px-3">
        {groups.map((group) => (
          <div key={group.title} className="space-y-0.5 mb-1">
            {/* 分组标题 */}
            <div
              className="px-3.5 pt-3 pb-1.5 text-slate-500"
              style={{
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {group.title}
            </div>
            {/* 菜单项 */}
            {group.items.map((item) => (
              <NavItem
                key={item.to}
                icon={item.icon}
                label={item.label}
                badge={item.badge}
                active={isActive(item.to)}
                onClick={() => onNavigate(item.to)}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* 底部：下次抓取信息 */}
      <div className="px-4 py-4 mt-2 mx-3 text-[11px] text-slate-500 border-t border-white/10">
        <div>下次定时抓取</div>
        <div className="text-slate-300 font-mono mt-1">下周一 00:00 CST</div>
        <div className="mt-2 text-emerald-400">● 抓取系统正常运行</div>
      </div>
    </>
  );
}

// =========================================================
// 周次格式化：2026-W19 → "2026-W19 (5/4 – 5/10)"
// =========================================================
function formatWeekLabel(week: string): string {
  const m = week.match(/^(\d{4})-W(\d{1,2})$/);
  if (!m) return week;
  const year = parseInt(m[1], 10);
  const wk = parseInt(m[2], 10);
  // ISO week: 第 1 周包含 1 月 4 日
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay() || 7; // 周一=1, 周日=7
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - jan4Day + 1);
  const monday = new Date(week1Monday);
  monday.setDate(week1Monday.getDate() + (wk - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
  return `${week} (${fmt(monday)} – ${fmt(sunday)})`;
}

// =========================================================
// 主 Layout
// =========================================================
export default function AppLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { week, setWeek, recentWeeks, isLoaded, loadAvailableWeeks, fetchHistory, history } =
    useWeekStore();
  const username = useAuthStore((s) => s.user?.username);
  const role = useAuthStore((s) => s.user?.role);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  // RBAC: 「配置中心」整组 admin-only — viewer 不渲染（route 守卫 RequireAdmin 兜底）。
  const navGroups = useMemo(
    () => (role === 'admin' ? NAV_GROUPS : NAV_GROUPS.filter((g) => g.title !== '配置中心')),
    [role],
  );

  // 9.2f: <992 用 Drawer + hamburger,>=992 保现 Sider
  const { isDesktop } = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 切到 desktop 时确保 Drawer 关闭(避免残留打开态)
  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore — 即便 logout 请求失败也清本地 + 跳登录
    }
    clearAuth();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    if (!isLoaded) {
      void loadAvailableWeeks();
    }
  }, [isLoaded, loadAvailableWeeks]);

  // PR 9.2g: 拉 8 周历史完整度(Overview / BrandDetail 消费 placeholder）
  useEffect(() => {
    void fetchHistory(8);
  }, [fetchHistory]);

  const weekOptions = useMemo(() => {
    // PR 9.2k(owner 红线): data_not_ready 周(已结束但 metrics 未生成)→ disable + 标注,
    // 不让 owner 选到会显空/0 的周。history 只覆盖近 8 周;更早的周默认 ready。
    const notReady = notReadyWeekSet(history);
    return recentWeeks(26).map((w) => ({
      value: w,
      label: notReady.has(w) ? `${formatWeekLabel(w)}· 数据采集中` : formatWeekLabel(w),
      disabled: notReady.has(w),
    }));
    // recentWeeks is a stable selector but its output depends on availableWeeks;
    // re-derive when load flips to true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentWeeks, isLoaded, history]);

  // 当前路径对应的面包屑
  const crumb = (() => {
    if (pathname === '/') return PATH_LABEL['/overview'];
    const top = '/' + pathname.split('/')[1];
    return PATH_LABEL[top] ?? { group: '—', page: '—' };
  })();

  // 哪一项 active（一级路径匹配）
  const isActive = (to: string) => {
    if (to === '/overview' && (pathname === '/' || pathname.startsWith('/overview'))) {
      return true;
    }
    const topOfTo = '/' + to.split('/')[1];
    const topOfPath = '/' + pathname.split('/')[1];
    return topOfTo === topOfPath && (to === topOfTo || pathname.startsWith(to));
  };

  return (
    <Layout className="min-h-screen">
      {/* ============ Header ============ */}
      <Header
        className="!bg-white border-b border-slate-200 flex items-center !px-3 lg:!px-5"
        style={{ height: 56, lineHeight: 'normal' }}
      >
        {/* 9.2f: <lg hamburger 打开 Drawer */}
        {!isDesktop && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            aria-label="打开导航"
            className="mr-1 flex-shrink-0"
          />
        )}

        {/* Logo 区（mobile 隐藏文字,保 GAC icon;desktop 固定 240 对齐 Sider） */}
        <div className="flex items-center gap-2 flex-shrink-0 lg:w-60">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-gac-primary to-gac-primary-2 flex items-center justify-center text-white font-bold text-sm">
            GAC
          </div>
          <div className="hidden lg:block">
            <div className="text-[13.5px] font-semibold leading-tight text-slate-900">
              VOC / 口碑监控系统
            </div>
            <div className="text-[11px] text-slate-500 leading-tight mt-0.5">
              演示版本 · v2.0
            </div>
          </div>
        </div>

        {/* 面包屑（mobile 隐藏） */}
        <div className="flex-1 hidden lg:flex items-center gap-2 ml-2">
          <span className="text-[12.5px] text-slate-400">/ {crumb.group} /</span>
          <span className="text-[13px] text-slate-700 font-medium">{crumb.page}</span>
        </div>

        {/* mobile 占位撑开右侧工具区 */}
        <div className="flex-1 lg:hidden" />

        {/* 右侧工具区 */}
        <div className="flex items-center gap-2 lg:gap-3">
          {/* 周次选择器（mobile 隐藏文字标签 + 收窄;在 Drawer 内不另放,保单一来源） */}
          <div className="flex items-center gap-2 px-2 lg:px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
            <span className="hidden sm:inline text-[12px] text-slate-500">报告周</span>
            <Select
              value={week}
              onChange={setWeek}
              options={weekOptions}
              variant="borderless"
              size="small"
              className="w-[110px] sm:w-[170px]"
            />
          </div>

          {/* 手动抓取（mobile 收为 icon-only） */}
          <Button icon={<ReloadOutlined />} className="!px-2 lg:!px-4">
            <span className="hidden lg:inline">手动抓取</span>
          </Button>

          {/* 预警铃铛 */}
          <Badge count={5} size="small">
            <Button shape="circle" icon={<BellOutlined />} />
          </Badge>

          {/* 用户区 */}
          <Dropdown
            menu={{
              items: [
                {
                  key: 'change-password',
                  label: '修改密码',
                  onClick: () => navigate('/account/change-password'),
                },
                { type: 'divider' as const },
                { key: 'logout', label: '退出登录', onClick: handleLogout },
              ],
            }}
          >
            <div className="flex items-center gap-2 pl-2 lg:pl-3 cursor-pointer border-l border-slate-200">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-[12px] font-semibold flex-shrink-0">
                {(username || '—').slice(0, 2).toUpperCase()}
              </div>
              <div className="hidden lg:block text-[13px] leading-tight">
                <div className="font-medium text-slate-800">{username || '—'}</div>
                <div className="text-[11px] text-slate-500">已登录</div>
              </div>
            </div>
          </Dropdown>
        </div>
      </Header>

      <Layout>
        {/* ============ Sidebar（>=lg 固定 Sider,0 回归） ============ */}
        {isDesktop && (
          <Sider
            width={240}
            style={{ background: '#0B1F3D' }}
            className="!min-h-[calc(100vh-56px)]"
          >
            <SideNav isActive={isActive} onNavigate={(to) => navigate(to)} groups={navGroups} />
          </Sider>
        )}

        {/* ============ Drawer（<lg 移动端导航） ============ */}
        <Drawer
          placement="left"
          open={!isDesktop && drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width="min(86vw, 320px)"
          closable={false}
          styles={{ body: { padding: 0, background: '#0B1F3D' } }}
        >
          <SideNav
            isActive={isActive}
            onNavigate={(to) => {
              navigate(to);
              setDrawerOpen(false);
            }}
            groups={navGroups}
          />
        </Drawer>

        {/* ============ Main Content ============ */}
        <Content className="p-4 lg:p-6 overflow-x-hidden">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
